// Top part remains unchanged
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateExhibitorProduct } from '../../../../Reducer/exhibitorProductSlice';
import { toast } from 'react-toastify';
import axios from 'axios';

import ProductCategoriesDrawer from '../ProductCategories.jsx/ProductCategoriesDrawer.jsx';
import BasicInformation from './BasicInformation.jsx';
import BannerStatements from './BannerStatements.jsx';
import ImagesAndFiles from './ImagesAndFiles.jsx';
import ProductDescription from './ProductDescription.jsx';
import WhyChooseUs from './WhyChooseUs.jsx';
import FrequentlyAskedQuestions from './FrequentlyAskedQuestions.jsx';
import VariationsSection from './VariationsSection.jsx';
import ProductNotesSection from './ProductNotesSection.jsx';
import CreditContent from './CreditContent.jsx';

import 'quill/dist/quill.snow.css';
import {
  Button,
  CircularProgress,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
  styled
} from '@mui/material';

import {
  Category as CategoryIcon,
  East as EastIcon,
} from '@mui/icons-material';

const StatusSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.success.main,
    '& + .MuiSwitch-track': {
      backgroundColor: theme.palette.success.main,
    },
  },
}));

const EditProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  const [formData, setFormData] = useState({});
  const [product, setProduct] = useState(null);
  const [backgroundImg, setBackgroundImg] = useState(null);
  const [bannerImg, setBannerImg] = useState(null);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [expandedSection, setExpandedSection] = useState({
    basicInfo: false,
    bannerStatements: false,
    images: false,
    description: false,
    features: false,
    faq: false,
    variations: false,
    notes: true,
    creditContent: true
  });



  useEffect(() => {
    if (product) {
      setFormData({
        event_name: product.event_name,
        location: product.location,
        total_exhibitor: product.total_exhibitor,
        price: product.price,
        industries: product.industries,
        above_banner_statement: product.above_banner_statement || '',
        below_banner_statement: product.below_banner_statement || '',
        description: product.description || '',
        slug: product.slug,
        events_date: product.events_date,
        events_date_start: product.events_date_start,
        events_date_end: product.events_date_end,
        faq_1_question: product.faq_1_question || '',
        faq_1_answer: product.faq_1_answer || '',
        faq_2_question: product.faq_2_question || '',
        faq_2_answer: product.faq_2_answer || '',
        whychoose_1: product.whychoose_1 || '',
        whychoose_2: product.whychoose_2 || '',
        whychoose_3: product.whychoose_3 || '',
        whychoose_4: product.whychoose_4 || '',
        product_type: product.product_type || '',
        country: product.country || '',
        woocommerce_product_id: product.woocommerce_product_id || '',
        credits: product.credits || 0,
        content_box_1: product.content_box_1 || '',
        content_box_2: product.content_box_2 || '',
        tier: product.tier || '',
        tier_level: product.tier_level || '',
        badge: product.badge || '',
        tier_description: product.tier_description || '',
      });

      setBackgroundImg(product.background_img || null);
      setBannerImg(product.banner_img || null);
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBackgroundImgChange = (e) => {
    const file = e.target.files[0];
    setBackgroundFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setBackgroundImg(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleBannerImgChange = (e) => {
    const file = e.target.files[0];
    setBannerFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setBannerImg(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCsvChange = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
  };

  const handleDescriptionChange = (value) => {
    setFormData(prev => ({ ...prev, description: value }));
  };

  const handleCreditContent1Change = (value) => {
    setFormData(prev => ({ ...prev, content_box_1: value }));
  };

  const handleCreditContent2Change = (value) => {
    setFormData(prev => ({ ...prev, content_box_2: value }));
  };

  const toggleSection = (section) => {
    setExpandedSection(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleUpdateStatus = async () => {
    if (!product) return;

    const newStatus = product.status === 'active' ? 'inactive' : 'active';

    try {
      const response = await axios.put(`/api/update-exproduct-status/${product.ex_product_id}`,
        { status: newStatus },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setProduct(prev => ({ ...prev, status: newStatus }));
      } else {
        toast.error(response.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    const updatedFormData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      updatedFormData.append(key, value);
    });
    if (backgroundFile) updatedFormData.append('background_img', backgroundFile);
    if (bannerFile) updatedFormData.append('banner_img', bannerFile);
    if (csvFile) updatedFormData.append('csv', csvFile);

    dispatch(updateExhibitorProduct({ id: product?.ex_product_id, updatedProduct: updatedFormData }))
      .unwrap()
      .then((response) => {
        toast.success(response?.message || 'Product updated successfully');
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to update product:', error);
        const errorMessage =
          error?.message || error?.data?.message || 'Failed to update product. Please try again.';
        toast.error(errorMessage);
        setLoading(false);
      });
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`/api/get-single-product/${id}`);
        const data = await res.json();
        setProduct(data);
      } catch (err) {
        console.error('Failed to fetch product:', err);
        toast.error('Failed to load product data.');
        navigate('/dashboard/products', { replace: true });
      }
    };

    fetchProduct();
  }, [id, navigate]);

  if (!product) return <Typography sx={{ p: 4 }}>Loading product...</Typography>;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        mb: { xs: '80%', sm: 4 },
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          px: { xs: 2, sm: 3 },
          py: 2,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          border: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
          Edit Product: {formData.event_name}
        </Typography>
        <Button
          startIcon={<EastIcon sx={{ transform: 'rotate(180deg)' }} />}
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#1e5eff',
            borderColor: '#1e5eff',
            '&:hover': {
              backgroundColor: '#f0f4ff',
              borderColor: '#1e5eff'
            }
          }}
        >
          Back
        </Button>
      </Box>

      {/* Categories */}
      <Box
        sx={{
          mb: 4,
          px: { xs: 2, sm: 3 },
          py: 2,
          borderRadius: 2,
          boxShadow: '0px 1px 5px rgba(0,0,0,0.1)',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          border: '1px solid #e0e0e0',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, fontSize: '1.125rem', color: '#000' }}
        >
          Product Categories
        </Typography>

        <FormControlLabel
          control={
            <StatusSwitch
              checked={product.status === 'active'}
              onChange={handleUpdateStatus}
              name="status"
            />
          }
          label={
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Status: <span style={{ color: product.status === 'active' ? 'green' : 'red' }}>
                {product.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </Typography>
          }
          sx={{ mr: 'auto', ml: 2 }}
        />


        <Button
          variant="contained"
          onClick={() => setOpenDrawer(true)}
          startIcon={<CategoryIcon />}
          sx={{
            bgcolor: '#1e5eff',
            '&:hover': { bgcolor: '#1a4edb' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            px: 2.5,
            py: 1
          }}
        >
          Manage Categories
        </Button>

        <ProductCategoriesDrawer
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          productId={product.ex_product_id}
        />
      </Box>

      {/* Form Sections */}
      <form onSubmit={handleSubmit}>
        <BasicInformation
          formData={formData}
          handleChange={handleChange}
          expanded={expandedSection.basicInfo}
          toggleSection={toggleSection}
        />

        {formData.product_type === 'credit' && (
          <CreditContent
            formData={formData}
            handleCreditContent1Change={handleCreditContent1Change}
            handleCreditContent2Change={handleCreditContent2Change}
            expanded={expandedSection.creditContent}
            toggleSection={toggleSection}
          />
        )}

        {formData.product_type !== 'credit' && (
          <>
            <BannerStatements
              formData={formData}
              handleChange={handleChange}
              expanded={expandedSection.bannerStatements}
              toggleSection={toggleSection}
            />

            <ImagesAndFiles
              bannerImg={bannerImg}
              backgroundImg={backgroundImg}
              csvFile={csvFile}
              handleBannerImgChange={handleBannerImgChange}
              handleBackgroundImgChange={handleBackgroundImgChange}
              handleCsvChange={handleCsvChange}
              expanded={expandedSection.images}
              toggleSection={toggleSection}
            />

            <ProductDescription
              description={formData.description}
              onDescriptionChange={handleDescriptionChange}
              expanded={expandedSection.description}
              toggleSection={toggleSection}
            />

            <WhyChooseUs
              formData={formData}
              handleChange={handleChange}
              expanded={expandedSection.features}
              toggleSection={toggleSection}
            />

            <FrequentlyAskedQuestions
              formData={formData}
              handleChange={handleChange}
              expanded={expandedSection.faq}
              toggleSection={toggleSection}
            />
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            sx={{
              minWidth: 200,
              height: 48,
              position: 'relative'
            }}
          >
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  color: 'white',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px'
                }}
              />
            )}
            <span style={{ visibility: loading ? 'hidden' : 'visible' }}>
              Update Product
            </span>
          </Button>
        </Box>

      </form>

      {formData.product_type !== 'credit' && (
        <VariationsSection
          product={product?.woocommerce_product_id}
          expanded={expandedSection.variations}
          toggleSection={toggleSection}
        />
      )}

      <ProductNotesSection
        productId={product.ex_product_id}
        expanded={expandedSection.notes}
        toggleSection={toggleSection}
      />
    </Box>
  );
};

export default EditProductPage;
